import React, { useMemo } from "react"
import { Platform } from "react-native"

import {
  useSelection,
  useUpdateSelectionConfigEffect,
} from "~shared/components/providers/SelectionProvider"

import { DEFAULT_KEY } from "../../constants"
import { Key, KeyLabels } from "../../container/Menu"
import { useDisplayValue } from "../../hooks/useDisplayValue"
import { IconConfig, TextInput, TextInputProps } from "./TextInput"

type SelectionInputProps = Omit<
  TextInputProps,
  "mode" | "iconLeft" | "iconRight" | "onChange"
> & {
  displayKeyValuePairs: KeyLabels

  title?: string
  tooltip?: string
  clearable?: boolean
  enableSearch?: boolean
  excludedValues?: Key[]
}

type SingleSelectionInputProps = SelectionInputProps & {
  value?: Key
  onChange?(value: Key): void
}

type MultiSelectionInputProps = SelectionInputProps & {
  values: Key[]
  category: string

  onChange?(values: Key[]): void
}

type SelectionInputInternalProps = (
  | SingleSelectionInputProps
  | MultiSelectionInputProps
) & { displayValue: string }

export { SingleSelectionInput, MultiSelectionInput, MultiSelectionInputProps }

function SingleSelectionInput(props: SingleSelectionInputProps) {
  const { value, displayKeyValuePairs } = props

  return (
    <InternalSelectionInput
      {...props}
      displayValue={value ? displayKeyValuePairs[value] : ""}
    />
  )
}

function MultiSelectionInput(props: MultiSelectionInputProps) {
  return (
    <InternalSelectionInput
      {...props}
      displayValue={useDisplayValue(props)}
      excludedValues={[DEFAULT_KEY].concat(props.excludedValues ?? [])}
    />
  )
}

function InternalSelectionInput(props: SelectionInputInternalProps) {
  const { iconRight, textInputProps, onSelect } = useTextInputProps(props)

  return (
    <TextInput
      {...textInputProps}
      editable={false}
      iconRight={iconRight}
      value={props.displayValue}
      pointerEvents={Platform.OS == "web" ? "none" : undefined}
      onChange={() => {}}
      // Required for web (does not support `onPressIn`)
      onPress={onSelect}
      // Required for app (does not support `onPress`)
      onPressIn={onSelect}
    />
  )
}

function useTextInputProps(props: SelectionInputInternalProps) {
  const {
    title,
    tooltip,
    clearable,
    enableSearch,
    displayValue,
    excludedValues,
    displayKeyValuePairs,
    ...textInputProps
  } = props

  const { key, selectSingle, selectMulti } = useSelection()

  // Shared selection parts

  const config = useMemo(() => {
    const keys = Object.keys(displayKeyValuePairs)

    return {
      key,
      title,
      tooltip,
      items:
        excludedValues && excludedValues.length > 0
          ? keys.filter(key => !excludedValues.includes(key))
          : keys,
      filterSelector: enableSearch
        ? (key: Key) => [displayKeyValuePairs[key]]
        : undefined,
      displayMapping: (key: Key) => displayKeyValuePairs[key],
    }
  }, [displayKeyValuePairs, enableSearch, excludedValues, key, title, tooltip])
  useUpdateSelectionConfigEffect(config)

  const showListIcon = !displayValue || !clearable
  const clearIcon = { name: "close" } as IconConfig
  const listIcon = { name: "format-list-checkbox" } as IconConfig

  // Single selection branch

  if (!("category" in props)) {
    const { value, onChange } = props

    const iconRight = showListIcon
      ? listIcon
      : { ...clearIcon, onPress: () => onChange?.("") }

    const onSelect = () =>
      selectSingle({ ...config, selection: value }).then(onChange)

    return { iconRight, textInputProps, onSelect }
  }

  // Multi selection branch

  const { values, onChange } = props

  const iconRight =
    showListIcon || values.filter(key => key != DEFAULT_KEY).length <= 0
      ? listIcon
      : { ...clearIcon, onPress: () => onChange?.([DEFAULT_KEY]) }

  const onSelect = () =>
    selectMulti({ ...config, selection: values ? [...values] : [] }).then(
      selection => {
        const sanitizedSelection = selection.filter(key => key != DEFAULT_KEY)
        onChange?.(
          sanitizedSelection.length > 0 ? sanitizedSelection : [DEFAULT_KEY],
        )
      },
    )

  return { iconRight, textInputProps, onSelect }
}
